import {
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Flex,
} from "@chakra-ui/react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useApolloClient, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import * as userSchema from "utils/schemas/user";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import { serverGql } from "__generated__/serverGql";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { assertBrandId, assertPresence } from "utils/assertions";
import serverClient from "utils/apollo/serverClient";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { gql } from "__generated__/gql";

const FORM_ID = "invite-user-form";

const mutation = serverGql(/* ServerGraphQL */ `
  mutation InviteBrandUser($brandId: UUID!, $user: UserInput!) {
    inviteBrandUser(brandId: $brandId, user: $user) {
      brandUser {
        id
        brandId
        createdAt
        user {
          id
          email
          firstName
          lastName
          fullName
          image
          inviteAcceptedAt
        }
      }
    }
  }
`);

const cacheUpdateFragment = gql(/* GraphQL */ `
  fragment InviteBrandUserCacheUpdate on BrandUser {
    id
    brandId
    createdAt
    user {
      id
      email
      firstName
      lastName
      fullName
      image
      inviteAcceptedAt
    }
  }
`);

type FormInputs = {
  firstName: string;
  lastName: string;
  email: string;
};

const schema: yup.ObjectSchema<FormInputs> = yup.object().shape({
  firstName: userSchema.firstName,
  lastName: userSchema.lastName,
  email: userSchema.email,
});

const { FormField } = createFormFieldComponents<FormInputs>({ schema });

type Props = {
  onClose: () => void;
};

export default function InviteModalContent({ onClose }: Props) {
  const brandId = useCurrentBrandId();
  const client = useApolloClient();

  const formContext = useForm<FormInputs>({ resolver: yupResolver(schema) });

  const { showErrorToast } = useAsyncCompleteToast();

  const [performMutation, { loading }] = useMutation(mutation, {
    client: serverClient,
  });

  const handleValidSubmit = (formData: FormInputs) => {
    assertBrandId(brandId);
    performMutation({ variables: { brandId, user: formData } })
      .then((result) => {
        const { brandUser } = result.data?.inviteBrandUser || {};
        assertPresence(brandUser);
        client.writeFragment({
          id: client.cache.identify(brandUser),
          fragment: cacheUpdateFragment,
          data: brandUser,
        });
        addNewRecordsWithFieldSpec({
          cache: client.cache,
          baseFieldName: "brandUsers",
          newObjs: [brandUser],
          getArgs: (existing) => ({ ...existing, condition: { brandId } }),
        });
        onClose();
      })
      .catch((reason) => {
        if (reason instanceof Error && reason.message.includes("email")) {
          formContext.setError("email", { message: reason.message });
        }
        showErrorToast(reason);
      });
  };

  return (
    <>
      <ModalHeader>Invite New User</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Form
          formContext={formContext}
          onValidSubmit={handleValidSubmit}
          id={FORM_ID}
        >
          <Flex gap={4}>
            <FormField name="firstName" mt={0} autoFocus />
            <FormField name="lastName" mt={0} />
          </Flex>
          <FormField
            name="email"
            renderInput={(props) => <Input type="email" {...props} />}
          />
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" mr={3} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          colorScheme="pinkButton"
          isLoading={loading}
        >
          Invite
        </Button>
      </ModalFooter>
    </>
  );
}
